import { useState, useEffect, useCallback, useRef } from 'react';

const VOICE_CALL_DURATION_MS = 60000;

export default function VoiceQuestionUI({
  socket,
  hallSlug,
  hallMeta,
  user,
  isCreatorHall: creatorHall
}) {
  const [credits, setCredits] = useState(0);
  const [payoutsReady, setPayoutsReady] = useState(false);
  const [connectBusy, setConnectBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [requestId, setRequestId] = useState(null);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [incomingRequest, setIncomingRequest] = useState(null);
  const [activeCall, setActiveCall] = useState(null);
  const [callTimer, setCallTimer] = useState(0);
  const timerRef = useRef(null);

  const isCreator = creatorHall && (
    user?.status === 'Owner'
    || (hallMeta?.ownerUserId != null && user?.userId != null
      && String(hallMeta.ownerUserId) === String(user.userId))
  );
  const isAuthenticated = !!user?.userId;

  const fetchCredits = useCallback(() => {
    if (!isAuthenticated || !hallSlug) return;
    fetch(`/api/vip-pass/status?hallSlug=${encodeURIComponent(hallSlug)}`, {
      credentials: 'include'
    })
      .then((r) => r.json())
      .then((data) => {
        setCredits(data.totalCredits || 0);
        if (typeof data.payoutsReady === 'boolean') setPayoutsReady(data.payoutsReady);
      })
      .catch(() => {});
  }, [isAuthenticated, hallSlug]);

  useEffect(() => {
    fetchCredits();
  }, [fetchCredits]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    let changed = false;
    if (url.searchParams.has('vip')) {
      url.searchParams.delete('vip');
      changed = true;
    }
    if (url.pathname.startsWith('/%40')) {
      url.pathname = `/@${decodeURIComponent(url.pathname.slice('/%40'.length))}`;
      changed = true;
    }
    if (changed) {
      const next = `${url.pathname}${url.search}${url.hash}`;
      window.history.replaceState(null, '', next);
    }
  }, []);

  useEffect(() => {
    if (!creatorHall || !hallSlug) return undefined;
    let cancelled = false;
    fetch(`/api/halls/${encodeURIComponent(hallSlug)}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled && typeof data?.payoutsReady === 'boolean') {
          setPayoutsReady(data.payoutsReady);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [creatorHall, hallSlug]);

  const startStripeOnboard = async () => {
    if (connectBusy) return;
    setConnectBusy(true);
    try {
      const res = await fetch('/api/stripe/connect/onboard', {
        method: 'POST',
        credentials: 'include'
      });
      const data = await res.json().catch(() => ({}));
      if (data.url) {
        window.location.href = data.url;
        return;
      }
      alert(data.error || 'Could not start Stripe onboarding');
    } catch {
      alert('Could not start Stripe onboarding');
    } finally {
      setConnectBusy(false);
    }
  };

  useEffect(() => {
    if (!socket) return;
    const onSent = (data) => {
      setRequestId(data.requestId);
      setPendingStatus('pending');
    };
    const onExpired = (data) => {
      setPendingStatus('expired');
      setRequestId(null);
      setTimeout(() => setPendingStatus(null), 3000);
      setIncomingRequest((prev) =>
        prev?.requestId === data?.requestId ? null : prev
      );
    };
    const onDeclined = () => {
      setPendingStatus('declined');
      setRequestId(null);
      setTimeout(() => setPendingStatus(null), 3000);
    };
    const onError = (data) => {
      setPendingStatus(data?.reason || 'error');
      setTimeout(() => setPendingStatus(null), 3000);
    };
    const onIncoming = (data) => {
      setIncomingRequest(data);
    };
    const onStart = (data) => {
      setActiveCall(data);
      setPendingStatus(null);
      setIncomingRequest(null);
      setCallTimer(Math.ceil((data.durationMs || VOICE_CALL_DURATION_MS) / 1000));
    };
    const onEnd = () => {
      setActiveCall(null);
      setCallTimer(0);
      fetchCredits();
    };

    socket.on('hall-voice-request-sent', onSent);
    socket.on('hall-voice-expired', onExpired);
    socket.on('hall-voice-declined', onDeclined);
    socket.on('hall-voice-request-error', onError);
    socket.on('hall-voice-incoming', onIncoming);
    socket.on('hall-voice-start', onStart);
    socket.on('hall-voice-end', onEnd);
    return () => {
      socket.off('hall-voice-request-sent', onSent);
      socket.off('hall-voice-expired', onExpired);
      socket.off('hall-voice-declined', onDeclined);
      socket.off('hall-voice-request-error', onError);
      socket.off('hall-voice-incoming', onIncoming);
      socket.off('hall-voice-start', onStart);
      socket.off('hall-voice-end', onEnd);
    };
  }, [socket, fetchCredits]);

  useEffect(() => {
    if (!activeCall) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }
    timerRef.current = setInterval(() => {
      setCallTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [activeCall]);

  const handleBuyPass = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const res = await fetch('/api/vip-pass/checkout', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hallSlug })
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        alert(data.error || 'Checkout failed');
      }
    } catch {
      alert('Checkout failed');
    } finally {
      setLoading(false);
    }
  };

  const handleAskQuestion = () => {
    if (!socket || requestId || activeCall) return;
    socket.emit('hall-voice-request', { hallSlug });
  };

  const handleRespond = (accept) => {
    if (!socket || !incomingRequest) return;
    socket.emit('hall-voice-respond', {
      requestId: incomingRequest.requestId,
      accept
    });
    if (!accept) setIncomingRequest(null);
  };

  const handleEndCall = () => {
    if (!socket || !activeCall) return;
    socket.emit('hall-voice-end-early', { requestId: activeCall.requestId });
  };

  if (!creatorHall) return null;

  return (
    <>
      {/* Active voice call overlay */}
      {activeCall && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[160] bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl shadow-2xl px-5 py-3 text-white text-center min-w-[260px]">
          <div className="text-xs uppercase tracking-wider opacity-80 mb-1">Live Voice Q&A</div>
          <div className="text-2xl font-bold tabular-nums mb-1">
            {Math.floor(callTimer / 60)}:{String(callTimer % 60).padStart(2, '0')}
          </div>
          <div className="text-sm opacity-90 mb-2">
            {activeCall.fromUsername || 'Someone'} is asking a question
          </div>
          <button
            type="button"
            onClick={handleEndCall}
            className="bg-red-500 hover:bg-red-400 text-white text-xs font-semibold px-4 py-1.5 rounded-full"
          >
            End Call
          </button>
        </div>
      )}

      {/* Creator: incoming request popup */}
      {isCreator && incomingRequest && !activeCall && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[160] bg-gray-900 border border-purple-500 rounded-xl shadow-2xl px-5 py-4 text-white text-center min-w-[280px]">
          <div className="text-xs uppercase tracking-wider text-purple-400 mb-2">Voice Question Request</div>
          <div className="text-base font-semibold mb-1">
            {incomingRequest.fromUsername || 'Someone'}
          </div>
          <p className="text-xs text-gray-400 mb-3">
            wants a 60-second voice call with you
          </p>
          <div className="flex gap-2 justify-center">
            <button
              type="button"
              onClick={() => handleRespond(false)}
              className="bg-gray-700 hover:bg-gray-600 text-white text-sm font-medium px-4 py-2 rounded-lg"
            >
              Decline
            </button>
            <button
              type="button"
              onClick={() => handleRespond(true)}
              className="bg-green-600 hover:bg-green-500 text-white text-sm font-medium px-4 py-2 rounded-lg"
            >
              Accept
            </button>
          </div>
        </div>
      )}

      {isCreator && !payoutsReady && !activeCall && (
        <div className="fixed bottom-3 right-3 sm:bottom-4 sm:right-4 z-[100] flex flex-col items-end gap-2 max-w-[260px]">
          <div className="bg-gray-900/95 border border-amber-500/60 rounded-xl px-4 py-3 text-white text-sm shadow-lg">
            <div className="font-semibold mb-1">Sell VIP passes</div>
            <p className="text-xs text-gray-300 mb-3">
              Connect Stripe to get paid directly. BIGPLAYS keeps a $3 fee on each $10 pass.
            </p>
            <button
              type="button"
              onClick={startStripeOnboard}
              disabled={connectBusy}
              className="w-full bg-amber-500 hover:bg-amber-400 text-black font-semibold px-3 py-2 rounded-lg text-sm disabled:opacity-50"
            >
              {connectBusy ? 'Opening Stripe…' : 'Connect Stripe'}
            </button>
          </div>
        </div>
      )}

      {/* User: ask question / buy pass buttons (bottom-right floating) */}
      {!isCreator && isAuthenticated && !activeCall && (
        <div className="fixed bottom-3 right-3 sm:bottom-4 sm:right-4 z-[100] flex flex-col items-end gap-2">
          {pendingStatus === 'pending' && (
            <div className="bg-yellow-600/90 rounded-lg px-3 py-2 text-white text-xs shadow-lg">
              Waiting for creator to accept…
            </div>
          )}
          {pendingStatus === 'expired' && (
            <div className="bg-gray-600/90 rounded-lg px-3 py-2 text-white text-xs shadow-lg">
              Request timed out
            </div>
          )}
          {pendingStatus === 'declined' && (
            <div className="bg-red-600/90 rounded-lg px-3 py-2 text-white text-xs shadow-lg">
              Creator declined
            </div>
          )}
          {pendingStatus === 'no_credits' && (
            <div className="bg-gray-600/90 rounded-lg px-3 py-2 text-white text-xs shadow-lg">
              No credits remaining
            </div>
          )}

          {credits > 0 && !pendingStatus && (
            <button
              type="button"
              onClick={handleAskQuestion}
              className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white font-semibold px-4 py-2.5 rounded-xl shadow-lg text-sm flex items-center gap-2"
            >
              <span>🎤</span> Ask a Question
              <span className="bg-white/20 rounded-full px-2 py-0.5 text-xs">{credits}</span>
            </button>
          )}

          {credits === 0 && !pendingStatus && payoutsReady && (
            <button
              type="button"
              onClick={handleBuyPass}
              disabled={loading}
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white font-semibold px-2.5 py-1.5 sm:px-4 sm:py-2.5 rounded-lg sm:rounded-xl shadow-lg text-xs sm:text-sm disabled:opacity-50"
            >
              {loading ? 'Loading…' : '⭐ Buy VIP Pass'}
            </button>
          )}
          {credits === 0 && !pendingStatus && !payoutsReady && (
            <div className="bg-gray-800/90 rounded-lg px-3 py-2 text-white text-xs shadow-lg max-w-[220px]">
              VIP passes are not on sale yet
            </div>
          )}
        </div>
      )}
    </>
  );
}
